import React, { useState, useEffect } from 'react';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiMinus,
  FiInfo,
  FiCalendar,
  FiDownload
} from 'react-icons/fi';

export default function AdvancedAnalyticsChart({ 
  title, 
  data, 
  type = 'line', // line, bar, area, donut
  timeframe = '7d',
  height = 300,
  showDownload = false,
  showInfo = false,
  infoText = '',
  color = '#3B82F6',
  gradientColor = '#93C5FD'
}) {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [chartDimensions, setChartDimensions] = useState({ width: 0, height: 0 });

  // Calculate chart statistics
  const calculateStats = () => {
    if (!data || data.length === 0) return { trend: 0, change: 0, total: 0 };

    const values = data.map(d => {
      const val = Number(d.value);
      return isNaN(val) ? 0 : val;
    });
    
    const total = values.reduce((sum, val) => sum + val, 0);
    const recent = values.slice(-Math.min(values.length, 2));
    
    let change = 0;
    if (recent.length >= 2 && recent[0] !== 0) {
      change = ((recent[1] - recent[0]) / recent[0]) * 100;
      // Ensure change is a valid number
      if (!isFinite(change)) change = 0;
    }
    
    const trend = change > 0.1 ? 1 : change < -0.1 ? -1 : 0;

    return { trend, change, total };
  };

  const stats = calculateStats();

  // Generate SVG path for line/area charts
  const generatePath = () => {
    if (!data || data.length === 0) return '';
    if (chartDimensions.width === 0 || chartDimensions.height === 0) return '';

    const values = data.map(d => {
      const val = Number(d.value);
      return isNaN(val) ? 0 : val;
    });

    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    const range = maxValue - minValue || 1;

    const points = data.map((point, index) => {
      const value = Number(point.value);
      const safeValue = isNaN(value) ? 0 : value;
      
      const x = data.length > 1 ? 
        (index / (data.length - 1)) * (chartDimensions.width - 60) + 30 : 
        chartDimensions.width / 2;
      const y = chartDimensions.height - 60 - ((safeValue - minValue) / range) * (chartDimensions.height - 120);
      
      // Ensure coordinates are valid numbers
      const safeX = isFinite(x) ? x : 30;
      const safeY = isFinite(y) ? y : chartDimensions.height - 60;
      
      return `${safeX},${safeY}`;
    });

    if (type === 'area') {
      const path = `M ${points[0]} L ${points.join(' L ')} L ${chartDimensions.width - 30},${chartDimensions.height - 60} L 30,${chartDimensions.height - 60} Z`;
      return path;
    }

    return `M ${points.join(' L ')}`;
  };

  // Generate bars for bar chart
  const generateBars = () => {
    if (!data || data.length === 0) return [];
    if (chartDimensions.width === 0 || chartDimensions.height === 0) return [];

    const values = data.map(d => {
      const val = Number(d.value);
      return isNaN(val) ? 0 : val;
    });
    
    const maxValue = Math.max(...values);
    if (maxValue === 0) return [];
    
    const barWidth = (chartDimensions.width - 60) / data.length * 0.8;
    const barSpacing = (chartDimensions.width - 60) / data.length * 0.2;

    return data.map((point, index) => {
      const value = Number(point.value);
      const safeValue = isNaN(value) ? 0 : value;
      
      const height = (safeValue / maxValue) * (chartDimensions.height - 120);
      const x = 30 + (index * (barWidth + barSpacing)) + barSpacing / 2;
      const y = chartDimensions.height - 60 - height;

      return {
        x: isFinite(x) ? x : 30,
        y: isFinite(y) ? y : chartDimensions.height - 60,
        width: isFinite(barWidth) ? barWidth : 10,
        height: isFinite(height) ? height : 0,
        value: safeValue,
        label: point.label
      };
    });
  };

  // Generate donut chart segments
  const generateDonutSegments = () => {
    if (!data || data.length === 0) return [];

    const total = data.reduce((sum, d) => sum + (d.value || 0), 0);
    if (total === 0) return [];

    const centerX = chartDimensions.width / 2;
    const centerY = chartDimensions.height / 2;
    const outerRadius = Math.min(chartDimensions.width, chartDimensions.height) / 2 - 20;
    const innerRadius = outerRadius * 0.6;

    let currentAngle = -Math.PI / 2; // Start at top

    return data.map((segment, index) => {
      const percentage = (segment.value || 0) / total;
      const angle = percentage * 2 * Math.PI;
      
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      
      const x1 = centerX + outerRadius * Math.cos(startAngle);
      const y1 = centerY + outerRadius * Math.sin(startAngle);
      const x2 = centerX + outerRadius * Math.cos(endAngle);
      const y2 = centerY + outerRadius * Math.sin(endAngle);
      
      const x3 = centerX + innerRadius * Math.cos(endAngle);
      const y3 = centerY + innerRadius * Math.sin(endAngle);
      const x4 = centerX + innerRadius * Math.cos(startAngle);
      const y4 = centerY + innerRadius * Math.sin(startAngle);
      
      const largeArc = angle > Math.PI ? 1 : 0;
      
      const pathData = `
        M ${x1} ${y1}
        A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2}
        L ${x3} ${y3}
        A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
        Z
      `;

      currentAngle += angle;

      return {
        path: pathData,
        value: segment.value,
        label: segment.label,
        percentage: (percentage * 100).toFixed(1),
        color: segment.color || `hsl(${(index * 45) % 360}, 65%, 55%)`
      };
    });
  };

  const formatValue = (value) => {
    if (typeof value !== 'number') return value;
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString();
  };

  const formatChange = (change) => {
    if (Math.abs(change) < 0.1) return '0%';
    const sign = change > 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  const getTrendIcon = () => {
    if (stats.trend > 0) return <FiTrendingUp size={16} style={{ color: '#10B981' }} />;
    if (stats.trend < 0) return <FiTrendingDown size={16} style={{ color: '#EF4444' }} />;
    return <FiMinus size={16} style={{ color: '#6B7280' }} />;
  };

  const getTrendColor = () => {
    if (stats.trend > 0) return '#10B981';
    if (stats.trend < 0) return '#EF4444';
    return '#6B7280';
  };

  const handleDownload = () => {
    const csvContent = data.map(d => `${d.label},${d.value}`).join('\n');
    const blob = new Blob([`Label,Value\n${csvContent}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.replace(/\s+/g, '_').toLowerCase()}_${timeframe}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const updateDimensions = () => {
      setChartDimensions({ width: 400, height: height });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [height]);

  const renderChart = () => {
    if (!data || data.length === 0) {
      return (
        <div style={{
          height: height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#9CA3AF',
          fontSize: '0.875rem'
        }}>
          No data available
        </div>
      );
    }

    switch (type) {
      case 'line':
        return (
          <svg width="100%" height={height} viewBox={`0 0 ${chartDimensions.width} ${chartDimensions.height}`}>
            {/* Grid lines */}
            {[...Array(5)].map((_, i) => (
              <line
                key={i}
                x1={30}
                y1={60 + (i * (chartDimensions.height - 120) / 4)}
                x2={chartDimensions.width - 30}
                y2={60 + (i * (chartDimensions.height - 120) / 4)}
                stroke="#F3F4F6"
                strokeWidth={1}
              />
            ))}
            
            {/* Chart line */}
            <path
              d={generatePath()}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            
            {/* Data points */}
            {data.map((point, index) => {
              const values = data.map(d => {
                const val = Number(d.value);
                return isNaN(val) ? 0 : val;
              });
              
              const maxValue = Math.max(...values);
              const minValue = Math.min(...values);
              const range = maxValue - minValue || 1;
              
              const value = Number(point.value);
              const safeValue = isNaN(value) ? 0 : value;
              
              const x = data.length > 1 ? 
                (index / (data.length - 1)) * (chartDimensions.width - 60) + 30 : 
                chartDimensions.width / 2;
              const y = chartDimensions.height - 60 - ((safeValue - minValue) / range) * (chartDimensions.height - 120);
              
              const safeX = isFinite(x) ? x : 30;
              const safeY = isFinite(y) ? y : chartDimensions.height - 60;
              
              return (
                <circle
                  key={index}
                  cx={safeX}
                  cy={safeY}
                  r={4}
                  fill={color}
                  stroke="white"
                  strokeWidth={2}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredPoint({ ...point, x: safeX, y: safeY })}
                  onMouseLeave={() => setHoveredPoint(null)}
                />
              );
            })}
            
            {/* Tooltip */}
            {hoveredPoint && (
              <g>
                <rect
                  x={hoveredPoint.x - 40}
                  y={hoveredPoint.y - 35}
                  width={80}
                  height={25}
                  fill="rgba(0,0,0,0.8)"
                  rx={4}
                />
                <text
                  x={hoveredPoint.x}
                  y={hoveredPoint.y - 18}
                  fill="white"
                  fontSize="12"
                  textAnchor="middle"
                >
                  {formatValue(hoveredPoint.value)}
                </text>
              </g>
            )}
          </svg>
        );

      case 'area':
        return (
          <svg width="100%" height={height} viewBox={`0 0 ${chartDimensions.width} ${chartDimensions.height}`}>
            {/* Gradient definition */}
            <defs>
              <linearGradient id={`gradient-${title.replace(/\s+/g, '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                <stop offset="100%" stopColor={color} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            
            {/* Grid lines */}
            {[...Array(5)].map((_, i) => (
              <line
                key={i}
                x1={30}
                y1={60 + (i * (chartDimensions.height - 120) / 4)}
                x2={chartDimensions.width - 30}
                y2={60 + (i * (chartDimensions.height - 120) / 4)}
                stroke="#F3F4F6"
                strokeWidth={1}
              />
            ))}
            
            {/* Area fill */}
            <path
              d={generatePath()}
              fill={`url(#gradient-${title.replace(/\s+/g, '')})`}
              stroke={color}
              strokeWidth={2}
            />
          </svg>
        );

      case 'bar':
        const bars = generateBars();
        return (
          <svg width="100%" height={height} viewBox={`0 0 ${chartDimensions.width} ${chartDimensions.height}`}>
            {bars.map((bar, index) => (
              <rect
                key={index}
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                fill={color}
                rx={2}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredPoint({ ...bar, x: bar.x + bar.width / 2, y: bar.y })}
                onMouseLeave={() => setHoveredPoint(null)}
              />
            ))}
            
            {/* Tooltip */}
            {hoveredPoint && (
              <g>
                <rect
                  x={hoveredPoint.x - 40}
                  y={hoveredPoint.y - 35}
                  width={80}
                  height={25}
                  fill="rgba(0,0,0,0.8)"
                  rx={4}
                />
                <text
                  x={hoveredPoint.x}
                  y={hoveredPoint.y - 18}
                  fill="white"
                  fontSize="12"
                  textAnchor="middle"
                >
                  {formatValue(hoveredPoint.value)}
                </text>
              </g>
            )}
          </svg>
        );

      case 'donut':
        const segments = generateDonutSegments();
        return (
          <div style={{ display: 'flex', alignItems: 'center', height }}>
            <div style={{ flex: 1 }}>
              <svg width="100%" height={height} viewBox={`0 0 ${chartDimensions.width} ${chartDimensions.height * 0.8}`}>
                {segments.map((segment, index) => (
                  <path
                    key={index}
                    d={segment.path}
                    fill={segment.color}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredPoint(segment)}
                    onMouseLeave={() => setHoveredPoint(null)}
                  />
                ))}
                
                {/* Center text */}
                <text
                  x={chartDimensions.width / 2}
                  y={chartDimensions.height * 0.4}
                  textAnchor="middle"
                  fontSize="24"
                  fontWeight="600"
                  fill="#1F2937"
                >
                  {formatValue(stats.total)}
                </text>
                <text
                  x={chartDimensions.width / 2}
                  y={chartDimensions.height * 0.4 + 20}
                  textAnchor="middle"
                  fontSize="12"
                  fill="#6B7280"
                >
                  Total
                </text>
              </svg>
            </div>
            
            {/* Legend */}
            <div style={{ marginLeft: '1rem' }}>
              {segments.map((segment, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: segment.color,
                    borderRadius: '2px',
                    marginRight: '0.5rem'
                  }} />
                  <span style={{ color: '#374151' }}>
                    {segment.label} ({segment.percentage}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return <div>Unsupported chart type</div>;
    }
  };

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #E5E7EB',
      padding: '1.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#1F2937' }}>
            {title}
          </h3>
          {showInfo && infoText && (
            <div style={{ position: 'relative', display: 'inline-block' }} title={infoText}>
              <FiInfo size={16} style={{ color: '#9CA3AF', cursor: 'help' }} />
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {type !== 'donut' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.875rem',
              color: getTrendColor()
            }}>
              {getTrendIcon()}
              <span>{formatChange(stats.change)}</span>
            </div>
          )}
          
          {showDownload && (
            <button
              onClick={handleDownload}
              style={{
                padding: '0.25rem',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#6B7280',
                borderRadius: '4px'
              }}
              title="Download CSV"
            >
              <FiDownload size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Chart */}
      <div style={{ position: 'relative' }}>
        {renderChart()}
      </div>

      {/* Tooltip for donut chart */}
      {type === 'donut' && hoveredPoint && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '0.5rem 0.75rem',
          borderRadius: '6px',
          fontSize: '0.875rem',
          pointerEvents: 'none',
          zIndex: 10
        }}>
          {hoveredPoint.label}: {formatValue(hoveredPoint.value)} ({hoveredPoint.percentage}%)
        </div>
      )}
    </div>
  );
}